import type { ReactNode } from "react";
import { Badge } from "../atoms/Badge";
import { cn } from "../lib/cn";
import { humanizeEventType } from "../lib/vocab";
import { AlertBanner } from "../molecules/AlertBanner";
import { Skeleton } from "../molecules/Skeleton";

export interface TamperAlertItem {
	id: string;
	alertType: string;
	severity: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
	createdAt: string;
	batchRoot: string | null;
	chainId: number | null;
	loanCaseIdHash: string | null;
	eventType: string | null;
}

export interface TamperAlertFeedProps {
	alerts: TamperAlertItem[];
	loading?: boolean;
	emptyMessage?: string;
	onSelectAlert?: (alert: TamperAlertItem) => void;
	className?: string;
}

type BadgeVariant = "destructive" | "warning" | "secondary" | "default";

const SEVERITY_VARIANT: Record<TamperAlertItem["severity"], BadgeVariant> = {
	LOW: "secondary",
	MEDIUM: "warning",
	HIGH: "destructive",
	CRITICAL: "destructive",
};

const SEVERITY_LABEL: Record<TamperAlertItem["severity"], string> = {
	LOW: "Low",
	MEDIUM: "Medium",
	HIGH: "High",
	CRITICAL: "Critical",
};

/**
 * Plain-language copy for each integrity alert type. `label` names the problem
 * in everyday terms; `meaning` explains what it implies and what to do, so a
 * non-technical auditor never has to decode a raw enum. Unknown types fall back
 * to {@link describeAlert}.
 */
const ALERT_TYPE_COPY: Record<string, { label: string; meaning: string }> = {
	ROOT_MISMATCH: {
		label: "Records don't match the public ledger",
		meaning:
			"These sealed records no longer match what was published on the public ledger. Contact the issuer to review.",
	},
	ORPHAN_ANCHOR: {
		label: "Unexpected entry on the public ledger",
		meaning:
			"An entry appeared on the public ledger with no matching record on file. Contact the issuer to review.",
	},
	DUPLICATE_ROOT: {
		label: "Records published more than once",
		meaning:
			"The same group of records was published to the public ledger twice. Contact the issuer to review.",
	},
	OUT_OF_ORDER: {
		label: "Records published out of sequence",
		meaning:
			"A group of records was published before the one that should have come first. Contact the issuer to review.",
	},
	MISSING_ANCHOR: {
		label: "Records not yet on the public ledger",
		meaning:
			"These records are marked as published but can't be found on the public ledger. Contact the issuer to review.",
	},
	CHAIN_UNREACHABLE: {
		label: "Couldn't reach the public ledger",
		meaning:
			"We couldn't reach the public ledger to confirm these records. This is often temporary, so check again shortly.",
	},
	NON_USER_OF_RECORD: {
		label: "Recorded by an unexpected party",
		meaning:
			"This action was recorded by a party other than the expected one. Contact the issuer to review.",
	},
};

/** Sentence-case fallback for alert types not in {@link ALERT_TYPE_COPY}. */
function describeAlert(alertType: string): { label: string; meaning: string } {
	const known = ALERT_TYPE_COPY[alertType];
	if (known) {
		return known;
	}
	const spaced = alertType.replace(/_/g, " ").toLowerCase();
	return {
		label: spaced.charAt(0).toUpperCase() + spaced.slice(1),
		meaning:
			"This record could not be confirmed and needs review. Contact the issuer.",
	};
}

/**
 * Live feed of tamper/integrity alerts. Each row uses an error AlertBanner.
 * Renders a green empty state when no alerts are present.
 */
function TamperAlertFeed({
	alerts,
	loading = false,
	emptyMessage = "No tamper alerts",
	onSelectAlert,
	className,
}: TamperAlertFeedProps): ReactNode {
	if (loading) {
		return (
			<div className={cn("flex flex-col gap-3", className)}>
				{Array.from({ length: 3 }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
					<Skeleton key={i} className="h-16 w-full" />
				))}
			</div>
		);
	}

	if (alerts.length === 0) {
		return (
			<AlertBanner tone="success" className={className}>
				{emptyMessage}
			</AlertBanner>
		);
	}

	return (
		<ul
			className={cn("flex flex-col gap-3", className)}
			aria-label="Tamper alert feed"
		>
			{alerts.map((alert) => {
				const { label, meaning } = describeAlert(alert.alertType);
				return (
					<li key={alert.id}>
						<AlertBanner tone="error" title={label}>
							<div className="flex flex-col gap-2">
								<p className="text-sm text-foreground">{meaning}</p>
								<div className="flex flex-wrap items-center gap-2">
									<Badge variant={SEVERITY_VARIANT[alert.severity]}>
										{SEVERITY_LABEL[alert.severity]}
									</Badge>
									{alert.eventType && (
										<span className="text-xs text-mutedForeground">
											{humanizeEventType(alert.eventType)}
										</span>
									)}
									<span className="text-xs text-mutedForeground">
										{new Date(alert.createdAt).toLocaleString()}
									</span>
									{onSelectAlert && alert.batchRoot && (
										<button
											type="button"
											onClick={() => onSelectAlert(alert)}
											className="text-xs underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-ring"
										>
											View details
										</button>
									)}
								</div>
							</div>
						</AlertBanner>
					</li>
				);
			})}
		</ul>
	);
}
TamperAlertFeed.displayName = "TamperAlertFeed";

export default TamperAlertFeed;
export { TamperAlertFeed };
